// 📄 components/StepASNIdoc.tsx
import React from 'react';
import { AlertCircle } from 'lucide-react';
import { AsnFormData } from '../../../../../services/commerce/integrationService';

interface StepASNIdocProps {
  partnerNumber: string;
  partnerType: string;
  inboundPort: string;
  onChange: (field: keyof AsnFormData, value: any) => void;
  isInvalid: boolean;
}

export function StepASNIdoc({ partnerNumber, partnerType, inboundPort, onChange, isInvalid }: StepASNIdocProps) {
  return (
    <div className="space-y-4 animate-fadeIn">
      {/* Ruta SPRO / Transacción SAP */}
      <div className="bg-teal-500/5 border border-teal-500/10 rounded-xl p-3 space-y-1">
        <span className="text-[10px] font-bold text-teal-400 uppercase tracking-widest block">Ruta SPRO / Transacción SAP</span>
        <p className="font-mono text-xs text-gray-300">
          <span className="bg-zinc-800 px-1.5 py-0.5 rounded text-teal-300 font-bold mr-1.5">WE20</span>
          <span>SAP Menu → Tools → ALE → ALE Administration → Runtime Settings → Partner Profiles</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-teal-400">1. Inbound IDoc Setup — Partner Profile (WE20)</h3>
        <p className="text-sm text-gray-400 mt-1">
          Configura el perfil del interlocutor del sistema CIG como receptor del tipo de mensaje{' '}
          <code className="bg-black/30 px-1 rounded text-teal-300">DESADV</code> (Despacho / Advanced Shipping Notice).
          El puerto de entrada recibirá los IDocs del tipo básico{' '}
          <code className="bg-black/30 px-1 rounded text-teal-300">DELVRY07</code> desde SAP Ariba.
        </p>
      </div>

      {/* Badge de tipo de mensaje fijo */}
      <div className="flex items-center gap-3 bg-teal-500/10 border border-teal-500/20 rounded-xl px-4 py-3">
        <div className="flex flex-col">
          <span className="text-[10px] font-semibold text-teal-400 uppercase tracking-widest">Tipo de Mensaje IDoc</span>
          <span className="font-mono text-sm font-bold text-white mt-0.5">DESADV</span>
        </div>
        <div className="w-px h-8 bg-white/10 mx-2" />
        <div className="flex flex-col">
          <span className="text-[10px] font-semibold text-teal-400 uppercase tracking-widest">Tipo Básico</span>
          <span className="font-mono text-sm font-bold text-white mt-0.5">DELVRY07</span>
        </div>
        <div className="w-px h-8 bg-white/10 mx-2" />
        <div className="flex flex-col">
          <span className="text-[10px] font-semibold text-teal-400 uppercase tracking-widest">Dirección</span>
          <span className="font-mono text-sm font-bold text-teal-300 mt-0.5">INBOUND ↓</span>
        </div>
      </div>

      <div className="bg-white/5 p-4 rounded-xl border border-white/5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Número de Interlocutor (Partner No.) <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={partnerNumber}
            onChange={(e) => onChange('partnerNumber', e.target.value.toUpperCase())}
            placeholder="ej. ARIBA_CIG"
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-teal-500 ${
              isInvalid && !partnerNumber ? 'border-red-500' : 'border-white/10'
            }`}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Tipo de Interlocutor</label>
          <select
            value={partnerType}
            onChange={(e) => onChange('partnerType', e.target.value)}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-teal-500"
          >
            <option value="LS">LS - Logical System</option>
            <option value="KU">KU - Customer</option>
            <option value="LI">LI - Vendor</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Puerto de Entrada (Inbound Port) <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={inboundPort}
            onChange={(e) => onChange('inboundPort', e.target.value.toUpperCase())}
            placeholder="ej. A000000001"
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-teal-500 ${
              isInvalid && !inboundPort ? 'border-red-500' : 'border-white/10'
            }`}
          />
        </div>
      </div>

      <div className="bg-teal-500/10 border border-teal-500/20 rounded-xl p-4 text-teal-300 flex gap-3 text-sm">
        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Parámetros Inbound del IDoc en WE20</p>
          <p className="text-xs text-teal-400/80 mt-0.5">
            En la entrada de parámetros de entrada (Inbound Options) de WE20, asigna el tipo de mensaje{' '}
            <code className="bg-black/30 px-1 rounded">DESADV</code> con el tipo básico{' '}
            <code className="bg-black/30 px-1 rounded">DELVRY07</code>. El código de proceso se configurará en el Paso 2.
            El interlocutor debe coincidir exactamente con la configuración del CIG Managed Gateway.
          </p>
        </div>
      </div>
    </div>
  );
}
